import React, { useRef, useState } from 'react'

import Label from '@components/labels/Label'
import type { validatorFunc } from '@/utils/validatorProvider'
import Validator from '@/pages/form/validator/Validator'

import './LabelAndTextInput.css'

interface LabelAndTextInputProps {
	id: string
	name?: string
	type?: string
	label: string
	placeholder?: string
	value?: string
	readOnly?: Object
	onChange: (value: string) => void
	validator?: validatorFunc
	autocomplete?: string
	style?: React.CSSProperties
	/** Extra class name applied to the wrapper, for page-specific styling. */
	className?: string
	/** Hide the label visually (it stays available to screen readers). */
	hideLabel?: boolean
	/** Icon displayed inside the field, on the trailing edge (`<img>`, FontAwesomeIcon, ...). */
	icon?: React.ReactNode
}

const LabelAndTextInput = (props: LabelAndTextInputProps) => {
	const [errors, setErrors] = useState<string[]>([])
	// useRef, not createRef: createRef hands out a new object on every render, so
	// React keeps detaching the one the Validator registered on mount. It happens
	// to survive today because the registry took a snapshot of the node, but the
	// stable ref is what the registration actually relies on.
	const ref = useRef<HTMLInputElement>(null)

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		props.onChange(e.target.value)
	}

	const handleError = (errors: string[]) => {
		setErrors(errors)
	}

	return (
		<Validator id={props.id} validator={props.validator} reference={ref} onError={handleError}>
			<div
				style={props.style}
				className={['LabelAndTextInput', props.className ?? ''].filter(Boolean).join(' ')}
			>
				<Label htmlFor={props.id} className={props.hideLabel ? 'visuallyHidden' : undefined}>
					{props.label}
				</Label>
				<div className='LabelAndTextInputField'>
					<input
						type={props.type ?? 'text'}
						placeholder={props.placeholder}
						id={props.id}
						value={props.value}
						ref={ref}
						name={props.name ?? props.id}
						{...props.readOnly}
						onChange={handleChange}
						autoComplete={props.autocomplete ?? 'off'}
					/>
					{props.icon && <span className='LabelAndTextInputIcon'>{props.icon}</span>}
				</div>
				{errors.map((error, index) =>
					error !== '' ? (
						<span key={index} className='error'>
							{error}
						</span>
					) : null,
				)}
			</div>
		</Validator>
	)
}

export default LabelAndTextInput
